import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import ID from '../../../../types/id';
import ConsentChecklistTask from '../../../../interfaces/consent-checklist-task';
import { combineLatest, Subscription } from 'rxjs';
import { IndividualChecklistService } from '../individual-checklist.service';
import { formatDate } from '@angular/common';
import { Person } from '../../../../interfaces/person';
import { IndicatorState } from '../../../consent-requests/consent-requests.component';
import { checkPermission } from '../../../../pipes/permission.pipe';
import { EditDialogComponent } from '../edit-dialog/edit-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '../../../../components/confirmation-modal/confirmation-modal.component';
import { DefaultSnackBarService } from '../../../../services/default-snack-bar.service';

type TaskTableItem = {
  id: ID;
  indicator_state: IndicatorState;
  task: string;
  status: string;
  responsible_party: Person | string;
  due_date: string;
  started_date: string;
  notes: string;
  in_group: boolean;
  consentChecklistTask: ConsentChecklistTask;
};

@Component({
    selector: 'tasks-table',
    templateUrl: './tasks-table.component.html',
    styleUrls: ['./tasks-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TasksTableComponent {
  @Input() consent_request_id: ID;
  public tableItems: TaskTableItem[] = [];
  public columns = [
    'indicator',
    'task',
    'status',
    'due_date',
    'started_date',
    'responsible_party',
    'notes',
  ];
  public tasksSub: Subscription;
  public permissionsSub: Subscription;
  IndicatorState = IndicatorState;

  @Input() isUrgent: boolean;

  constructor(
    public checklistService: IndividualChecklistService,
    public dialog: MatDialog,
    private _snackBar: DefaultSnackBarService
  ) {}

  ngOnInit() {
    this.tasksSub = combineLatest({
      tasks: this.checklistService.consentChecklistTasks$,
      userGroups: this.checklistService.userGroups$,
    }).subscribe(({ tasks }) => this._formatTasks(tasks));
    this.permissionsSub = this.checklistService.permission$.subscribe(
      (permission) => {
        let actionPermission =
          checkPermission(permission, '_edit') ||
          checkPermission(permission, '_delete');
        if (actionPermission && this.columns.indexOf('action') === -1) {
          this.columns.push('action');
        }
      }
    );
  }
  deleteTask(task: ConsentChecklistTask) {
    let dialog = this.dialog.open(ConfirmationModalComponent, {
      data: {
        modalTitle: 'Delete Task',
        actionMessage: '',
        warningMessage: 'Permanently delete this task?',
        modalInformation: [],
      },
    });
    dialog.afterClosed().subscribe((data) => {
      if (data?.clicked === 'Ok') {
        let sub;
        sub = this.checklistService.deleteTask(task.id).subscribe((task) => {
          this._snackBar.open('Deleted task successfully');
          this.checklistService.refreshConsentChecklist(
            this.consent_request_id
          );
        });
      }
    });
  }
  openEditDialog(task: ConsentChecklistTask) {
    let dialog = this.dialog.open(EditDialogComponent, {
      width: '40vw',
      data: {
        isExisting: true,
        consent_request_id: this.consent_request_id,
        consentChecklistTask: task,
      },
    });
  }
  private _calculateIndicatorState(task: ConsentChecklistTask): IndicatorState {
    let status = this.checklistService.getStatus(
      task.consent_checklist_status_id
    );
    if (status && status.checklist_status === 'Complete') {
      return IndicatorState.SUCCESS;
    }
    if (
      (!status || status.checklist_status !== 'Not Applicable') &&
      task.due_date
    ) {
      if (task.past_due) {
        if (this.isUrgent) {
          return IndicatorState.ERROR;
        }
        return IndicatorState.ALERT;
      }
    }

    return IndicatorState.INITIAL;
  }
  private _formatTasks(tasks: ConsentChecklistTask[]) {
    this.tableItems = [];
    for (let task of tasks) {
      let item: TaskTableItem = {
        id: task.id,
        task: task.task,
        indicator_state: this._calculateIndicatorState(task),
        status:
          this.checklistService.getStatus(task.consent_checklist_status_id)
            ?.checklist_status ?? '',
        due_date: task.due_date
          ? formatDate(task.due_date, 'MM/dd/yyyy', 'en-US')
          : '\u2014',
        started_date: task.start_date
          ? formatDate(task.start_date, 'MM/dd/yyyy', 'en-US')
          : '\u2014',
        responsible_party: this.checklistService.getResponsibleParty(task),
        notes: task.checklist_notes,
        consentChecklistTask: task,
        in_group: this.checklistService.inGroup(
          task.group ? task.group.id : ''
        ),
      };
      this.tableItems.push(item);
    }
  }

  ngOnDestroy() {
    this.tasksSub?.unsubscribe();
  }
}
